import type { User, AuthToken, SubscriptionTier } from '@/types'

const TOKEN_KEY = 'access_token'
export const USER_KEY = 'user'

const AUTH_COOKIE = 'ps_auth'
const COOKIE_MAX_AGE = 60 * 60 * 24 * 30 // 30 days

// SEC-AUTH: sessionStorage key holding the server-issued Google OAuth `state`
// value between the redirect out to Google and the callback that returns. The
// callback compares the `state` query param against this to reject CSRF /
// mismatched redirects. sessionStorage (not localStorage) so it dies with the
// tab and never leaks across sign-in attempts in other tabs.
export const GOOGLE_OAUTH_STATE_KEY = 'google_oauth_state'

/**
 * NB-F193: Cookie flag policy for the `ps_auth` presence marker.
 *
 * This cookie value is the literal string '1'; it does NOT carry the
 * JWT (the JWT lives in localStorage and rides on the Authorization
 * header).  The cookie exists solely so the Next.js middleware can
 * answer "is there a session?" without a fetch round-trip.  The flag
 * policy applied below is:
 *
 *   Secure           : set in production; the browser refuses to ship
 *                      it over plain HTTP.  Skipped on localhost so
 *                      `next dev` over http://localhost still works.
 *   HttpOnly         : NOT set, by browser-API limitation. document.cookie
 *                      cannot set HttpOnly (only the server can via
 *                      Set-Cookie).  Acceptable because the cookie body
 *                      is a non-sensitive presence flag, not a token.
 *                      The real JWT never enters a cookie at all, so
 *                      JS-XSS theft of a credential is structurally
 *                      impossible from the cookie surface.
 *   SameSite=Strict  : upgraded from Lax.  The marker is only ever
 *                      consulted on same-site middleware redirects, so
 *                      Strict closes the small CSRF surface that Lax
 *                      leaves open on cross-site top-level navigations.
 *   Path=/           : middleware reads it on every route.
 *   Max-Age=30d      : matches the JWT refresh window.
 */
export function buildAuthCookie(value: string, maxAgeSeconds: number): string {
  const parts = [
    `${AUTH_COOKIE}=${value}`,
    'path=/',
    `max-age=${maxAgeSeconds}`,
    'SameSite=Strict',
  ]
  // Browser refuses Secure on http://localhost so we only emit it when
  // the page is actually served over HTTPS (production, preview).
  if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
    parts.push('Secure')
  }
  return parts.join('; ')
}

export function saveAuth(token: AuthToken, user: User): void {
  if (typeof window === 'undefined') return
  localStorage.setItem(TOKEN_KEY, token.access_token)
  localStorage.setItem(USER_KEY, JSON.stringify(user))
  document.cookie = buildAuthCookie('1', COOKIE_MAX_AGE)
  // NB-F8: broadcast so ObservabilityBoot re-syncs PostHog identify +
  // Sentry user without a page reload.
  window.dispatchEvent(new CustomEvent('ps:auth-change', { detail: { kind: 'login', user } }))
}

export function getToken(): string | null {
  if (typeof window === 'undefined') return null
  return localStorage.getItem(TOKEN_KEY)
}

export function getStoredUser(): User | null {
  if (typeof window === 'undefined') return null
  const raw = localStorage.getItem(USER_KEY)
  if (!raw) return null
  try {
    return JSON.parse(raw) as User
  } catch {
    return null
  }
}

export function clearAuth(): void {
  if (typeof window === 'undefined') return
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(USER_KEY)
  // Same flag policy on the delete-cookie write so a browser that
  // negotiated a Secure+Strict cookie at login doesn't refuse the
  // empty-value rewrite at logout (some browsers require the flag set
  // to match on overwrite).
  document.cookie = buildAuthCookie('', 0)
  // NB-F8: broadcast so ObservabilityBoot resets PostHog identity +
  // clears the Sentry user. Logout from any surface (Topbar, Sidebar,
  // Sessions page) triggers this.
  window.dispatchEvent(new CustomEvent('ps:auth-change', { detail: { kind: 'logout' } }))
}

export function isAuthenticated(): boolean {
  return !!getToken()
}

export function isAdmin(): boolean {
  const user = getStoredUser()
  return user?.subscription_tier === 'admin'
}

const PREVIEW_TIER_KEY = 'preview_tier'

export function getPreviewTier(): string | null {
  if (typeof window === 'undefined') return null
  return localStorage.getItem(PREVIEW_TIER_KEY)
}

export function setPreviewTier(tier: string | null): void {
  if (typeof window === 'undefined') return
  if (tier) localStorage.setItem(PREVIEW_TIER_KEY, tier)
  else localStorage.removeItem(PREVIEW_TIER_KEY)
}

/**
 * Returns the tier that should be used for UI gating decisions.
 * Admins default to 'admin' (highest rank, every gate passes) so no part of
 * the product shows them an "upgrade" CTA. Setting a preview tier in the
 * sidebar simulates a downgraded view; clearing preview restores full admin.
 * Non-admin users default to 'starter' if their subscription_tier is missing.
 */
export function getEffectiveTier(): SubscriptionTier {
  const user = getStoredUser()
  if (!user) return 'starter'
  if (user.subscription_tier === 'admin') {
    return (getPreviewTier() as SubscriptionTier) || 'admin'
  }
  return user.subscription_tier || 'starter'
}

// Tier rank for monotonic gating. Higher number = more capability.
// Canonical 5-tier model: starter < pro < business < scale, with admin top.
const TIER_RANK: Record<SubscriptionTier, number> = {
  starter: 1,
  pro: 2,
  business: 3,
  scale: 4,
  admin: 99,
}

/**
 * `tierAtLeast('pro')` is true for pro/business/scale/admin.
 * Use this instead of enumerating tiers in every gate.
 */
export function tierAtLeast(min: SubscriptionTier): boolean {
  const current = getEffectiveTier()
  return (TIER_RANK[current] ?? 0) >= (TIER_RANK[min] ?? 0)
}

// Admin preview-as picker options. Drives the Sidebar tier dropdown.
export const TIER_OPTIONS: { value: SubscriptionTier; label: string }[] = [
  { value: 'starter', label: 'Starter' },
  { value: 'pro', label: 'Pro' },
  { value: 'business', label: 'Business' },
  { value: 'scale', label: 'Scale' },
]
